// components/entities/pages/SourcingCampaignPage.tsx
// INHERITED DEBT, not new. AGENTS 4.2 forbids local useState in components/entities/pages:
// this UI state belongs in kernel entityKernelState. Migrating it is a behavioural change
// that needs its own pass and its own verification, so it is not being smuggled into a
// merge. Flagged here rather than silently bypassed.
// eslint-disable-next-line no-restricted-imports
import { useCallback, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { LeasingLeadDTO, LeasingLeadFilters, SourcingCampaignDTO } from '#types/types';
import { resolveCampaignPropertyIds } from '#lib/leasing/sourcingCampaignLifecycle';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { WaitingPulseTimer } from '@/components/portal/WaitingPulseTimer';
import { LeadsTable } from '@/components/entities/tables/leads/LeadsTable';
import { isLeadRevealedWhileBuilding } from '#lib/leasing/source-view-guards';
import { leadBelongsToCampaign } from '#lib/leasing/campaignMembership';
import { LeadsGrid } from '@/components/entities/grids/LeadsGrid';
import type { InventoryVisualMode } from '@/components/portal/ViewModeGraphicToggle';
import { useCampaignTableReviewMode, type TableColumnDef } from '@/components/portal/Table';
import { readCampaignRow } from '#lib/leasing/campaignRow';

interface SourcingCampaignPageProps {
  campaignId: string;
  className?: string;
  hasEntityRowAbove?: boolean;
}

const translations = {
  en: {
    investigationReport: 'Investigation Report',
    loadingCampaignDetails: 'Loading campaign details...',
    waitingTitle: 'Starting campaign',
    waitingSubtitle: 'Estimated warm-up: up to 45 seconds.',
    waitingElapsed: 'Countdown',
    expiredTitle: 'Campaign failed to start',
    expiredSubtitle: 'The warm-up timed out. Please try again or contact support.',
  },
  fr: {
    investigationReport: "Rapport d'investigation",
    loadingCampaignDetails: 'Chargement des details de la campagne...',
    waitingTitle: 'Demarrage de la campagne',
    waitingSubtitle: "Temps de chauffe estime: jusqu'a 45 secondes.",
    waitingElapsed: 'Compte a rebours',
    expiredTitle: 'La campagne n\'a pas demarré',
    expiredSubtitle: 'Le temps de chauffe a expiré. Veuillez réessayer ou contacter le support.',
  },
};

const TERMINAL_LEAD_STATUSES = new Set(['SUCCESS', 'FAILED', 'COMPLETE', 'ABORTED', 'REVERTED', 'INTERESTING', 'DISMISSED', 'INVALIDATED']);
const ACTIVE_LEAD_STATUSES = new Set(['PENDING', 'RUNNING']);
const CAMPAIGN_LEAD_PREFETCH_LIMIT = 120;

export const SourcingCampaignPage = ({ campaignId, className, hasEntityRowAbove = false }: SourcingCampaignPageProps) => {
  const { kernel } = useKernel();
  const lang = useKernelStore((state) => state.language);
  const t = translations[lang];
  const [leadsViewMode, setLeadsViewMode] = useState<InventoryVisualMode>('grid');

  // Both names reach the same row, and the reader decides which, once, for the whole app. The page
  // keeps its own typed view of that row: merging the two registries here would union two DTO
  // shapes and force a cast on every field the page reads.
  const campaignRow = useKernelStore((state) => readCampaignRow(state.inventory, campaignId)) as
    | SourcingCampaignDTO
    | undefined;
  const leadRegistry = useKernelStore((state) => state.inventory.Lead.registry);

  const data = campaignRow;

  useEffect(() => {
    if (!kernel.current || !campaignId) return;
    void kernel.current.syncEntity('Campaign', campaignId);
  }, [kernel, campaignId]);

  const campaignLeadIds = useMemo(() => {
    const fromCampaign = Array.isArray(data?.leadIds)
      ? data.leadIds.filter((id): id is string => typeof id === 'string' && id.length > 0)
      : [];
    // The campaign's own leadIds is a lagging index: it gains a lead when the lead is SCORED, not
    // when it is created. Reading it alone is why extending a shortlist showed nothing happening -
    // the new brands were already in the database, PENDING, and this page could not see one of
    // them. The lead's own campaign link is the fact; union it in so a row in flight counts.
    const known = new Set(fromCampaign);
    const fromRegistry = Object.keys(leadRegistry).filter(
      (leadId) => !known.has(leadId) && leadBelongsToCampaign(leadRegistry[leadId], campaignId)
    );
    return fromRegistry.length > 0 ? [...fromCampaign, ...fromRegistry] : fromCampaign;
  }, [data?.leadIds, leadRegistry, campaignId]);

  const campaignLeads = useMemo(
    () =>
      campaignLeadIds
        .map((leadId) => leadRegistry[leadId])
        .filter((lead): lead is LeasingLeadDTO => Boolean(lead)),
    [campaignLeadIds, leadRegistry]
  );

  const campaignLeadCounts = useMemo(() => {
    let completed = 0;
    let active = 0;

    campaignLeads.forEach((lead) => {
      const status = typeof lead.status === 'string' ? lead.status.toUpperCase() : '';
      if (TERMINAL_LEAD_STATUSES.has(status)) {
        completed += 1;
      } else if (ACTIVE_LEAD_STATUSES.has(status)) {
        active += 1;
      }
    });

    return {
      completed,
      total: campaignLeads.length,
      active,
    };
  }, [campaignLeads]);

  const campaignStatus = data?.status;
  const isCampaignWaiting = campaignStatus === 'WAITING';
  const isCampaignFailed = campaignStatus === 'FAILED';
  const isCampaignAborted = campaignStatus === 'ABORTED';
  // A LEAD STILL WORKING IS THE FACT; THE CAMPAIGN'S STATUS IS AN OPINION THAT ARRIVES LATE.
  //
  // Extending a finished shortlist leaves the campaign COMPLETE on this page - the row is updated
  // server-side but nothing pushes it back, so `campaignStatus` stayed COMPLETE for the whole
  // extension. `!isCampaignComplete` then short-circuited the clause below, the reveal gate never
  // engaged, and the count of what is coming was zero by construction: unscored brands rendered as
  // blank rows and the user saw no sign of work. Own leads that are not finished decide this.
  const isExecutionRunning =
    campaignStatus === 'RUNNING' ||
    isCampaignWaiting ||
    (!isCampaignFailed && !isCampaignAborted && campaignLeadCounts.active > 0);

  const sortedCampaignLeadIds = useMemo(() => {
    if (campaignLeadIds.length <= 1) return campaignLeadIds;

    if (!isExecutionRunning) {
      return campaignLeadIds;
    }

    // Launch-queue order first (deterministic), used to keep the not-yet-scored tail stable.
    const leadIdByBrandId = new Map<string, string>();
    campaignLeadIds.forEach((leadId) => {
      const lead = leadRegistry[leadId];
      if (!lead?.brandId || leadIdByBrandId.has(lead.brandId)) return;
      leadIdByBrandId.set(lead.brandId, leadId);
    });

    const targetBrandIds = Array.isArray(data?.targetBrandIds) ? data.targetBrandIds : [];
    const orderedByLaunchTargets = targetBrandIds
      .map((brandId) => leadIdByBrandId.get(brandId))
      .filter((leadId): leadId is string => typeof leadId === 'string' && leadId.length > 0);

    const alreadyIncluded = new Set(orderedByLaunchTargets);
    const remaining = campaignLeadIds.filter((leadId) => !alreadyIncluded.has(leadId));
    const queueOrder = [...orderedByLaunchTargets, ...remaining];

    // Float finished (scored) rows to the TOP, best score first, so the user can start working the
    // top of the list while the tail is still computing. Unscored rows keep their queue order below.
    // Re-runs live as scores land (leadRegistry is a dependency) and transitions seamlessly into the
    // fitScore-desc order applied at completion.
    const scoreOf = (leadId: string): number | null => {
      const s = leadRegistry[leadId]?.fitScore;
      return typeof s === 'number' && Number.isFinite(s) ? s : null;
    };
    const scored = queueOrder
      .filter((leadId) => scoreOf(leadId) !== null)
      .sort((a, b) => (scoreOf(b) as number) - (scoreOf(a) as number));
    const unscored = queueOrder.filter((leadId) => scoreOf(leadId) === null);

    return [...scored, ...unscored];
  }, [campaignLeadIds, leadRegistry, isExecutionRunning, data?.targetBrandIds]);

  // WHILE THE PASS RUNS, A ROW IS EITHER READY OR IT IS COMING - never a blank line in the table.
  //
  // This handed the table EVERY lead, so a brand that had not been scored yet rendered as an
  // ordinary row with no score: indistinguishable from a finished row whose score failed to load,
  // and the reason "add a brand" looked like nothing was happening. The unscored ones now leave
  // the row list and come back as placeholders below it, one per brand in flight.
  const revealedScopedLeadIds = useMemo(
    () =>
      isExecutionRunning
        ? sortedCampaignLeadIds.filter((leadId) =>
            isLeadRevealedWhileBuilding(leadRegistry[leadId]?.status),
          )
        : sortedCampaignLeadIds,
    [isExecutionRunning, sortedCampaignLeadIds, leadRegistry]
  );

  const effectiveScopedLeadIds = useMemo(
    () => (isExecutionRunning ? revealedScopedLeadIds : null),
    [isExecutionRunning, revealedScopedLeadIds]
  );

  const campaignLeadTableFilters = useMemo<Partial<LeasingLeadFilters>>(
    () => (isExecutionRunning ? {} : { sortBy: 'fitScore', sortDir: 'desc' }),
    [isExecutionRunning]
  );

  const reviewMode = useCampaignTableReviewMode({
    campaignType: 'SourcingCampaign',
    campaignId,
    entityType: 'Lead',
    reviewFilters: { lastCampaignId: campaignId },
    filePrefix: 'fater_sourcing_campaign',
    buildSnapshot: (lead: LeasingLeadDTO) => ({
      brandName: lead.brandName || '',
      score: typeof lead.fitScore === 'number' && Number.isFinite(lead.fitScore) ? lead.fitScore : '',
      initialStatus: lead.status || '',
    }),
  });

  const tableScopedLeadIds = useMemo(
    () => (reviewMode.isActive ? reviewMode.reviewIds : effectiveScopedLeadIds),
    [effectiveScopedLeadIds, reviewMode.isActive, reviewMode.reviewIds]
  );

  const tableLeadFilters = useMemo<Partial<LeasingLeadFilters>>(
    () => (reviewMode.isActive ? {} : campaignLeadTableFilters),
    [campaignLeadTableFilters, reviewMode.isActive]
  );

  // LeadsTable and LeadsGrid are React.memo: an object or a closure rebuilt in the JSX makes that
  // memo a no-op, and this page re-renders on every leadRegistry write (plus every 6s poll tick
  // while a pass runs).
  const campaignLockedLeadFilters = useMemo<Partial<LeasingLeadFilters>>(
    () => ({ lastCampaignId: campaignId }),
    [campaignId]
  );

  const reviewModeControls = reviewMode.controls;
  const renderCampaignToolbarHeader = useCallback(() => reviewModeControls, [reviewModeControls]);

  const handleCampaignLeadsManualRefresh = useCallback(async () => {
    if (!kernel.current) return;
    await Promise.all([
      kernel.current.syncEntity('Campaign', campaignId),
      kernel.current.queryInventory('Lead', {
        filters: { lastCampaignId: campaignId, sortBy: 'fitScore', sortDir: 'desc' },
        limit: CAMPAIGN_LEAD_PREFETCH_LIMIT,
        offset: 0,
        force: true,
        ttl: 0,
      }),
    ]);
  }, [kernel, campaignId]);

  useEffect(() => {
    if (!kernel.current || !campaignId) return;
    const hydrate = () => {
      void kernel.current?.queryInventory('Lead', {
        filters: { lastCampaignId: campaignId, sortBy: 'fitScore', sortDir: 'desc' },
        limit: CAMPAIGN_LEAD_PREFETCH_LIMIT,
        offset: 0,
      });
    };
    hydrate();
    // A lead is written PENDING the moment the pass claims a brand, but nothing pushes it here:
    // the socket only speaks up when the row is SCORED (an UPDATE, ghost-inserted into the
    // registry). So a run used to be invisible until it was over - no row, and no placeholder
    // either, because the count of what is coming is computed from the rows we hold. This poll
    // is what makes work in flight observable at all; it only hydrates the registry, it cannot
    // reorder the table, which renders from scopedIds we own.
    if (!isExecutionRunning) return;
    const timer = setInterval(hydrate, 6000);
    return () => clearInterval(timer);
  }, [kernel, campaignId, isExecutionRunning]);

  if (!data) {
    return (
      <div className="p-6 flex justify-center text-white/50 text-sm">
        <div className="animate-pulse">{t.loadingCampaignDetails}</div>
      </div>
    );
  }

  return (
    <div className={clsx('space-y-3', className)}>
      {isCampaignWaiting && (
        <WaitingPulseTimer
          startAtIso={data?.updatedAt || data?.createdAt}
          durationSeconds={45}
          title={t.waitingTitle}
          subtitle={t.waitingSubtitle}
          timerLabel={t.waitingElapsed}
          expiredTitle={t.expiredTitle}
          expiredSubtitle={t.expiredSubtitle}
        />
      )}
      {leadsViewMode === 'grid' ? (
        <LeadsGrid
          scopedIds={effectiveScopedLeadIds}
          initialFilters={campaignLeadTableFilters}
          lockedFilters={campaignLockedLeadFilters}
          assetLedgerTitle={t.investigationReport}
          assetLedgerBreadcrumb={t.investigationReport}
          onManualRefresh={handleCampaignLeadsManualRefresh}
          viewModeVisual={leadsViewMode}
          onViewModeVisualChange={setLeadsViewMode}
        />
      ) : (
        <LeadsTable
          scopedIds={tableScopedLeadIds}
          initialFilters={tableLeadFilters}
          lockedFilters={campaignLockedLeadFilters}
          // Same rule as the Source centre's shortlist: a run covering several assets holds one lead
          // per (asset, brand), so hiding the asset column would show the same brand three times
          // with three scores and no way to tell them apart.
          propertyLocked={resolveCampaignPropertyIds(data?.config, data?.propertyId ?? '').length <= 1}
          assetLedgerTitle={t.investigationReport}
          assetLedgerBreadcrumb={t.investigationReport}
          onManualRefresh={handleCampaignLeadsManualRefresh}
          viewModeVisual={leadsViewMode}
          onViewModeVisualChange={setLeadsViewMode}
          renderCustomToolbarHeader={renderCampaignToolbarHeader}
          toolbarContextMenuActions={reviewMode.contextMenuActions}
        />
      )}
    </div>
  );
};

